// @ts-nocheck
// Sticky section tabs for the /cti2 dashboards, tracking whichever section
// is under the stuck header as the scroll root moves. Extracted from
// AgentDashboardView so the team-lead dashboard uses the same nav.
// No scroll/step-button handling of its own — this renders inline inside
// Topbar's single shared scroller alongside AgentContextStrip's Groups/
// Campaigns/Schedule chips (see Topbar.tsx), one unified scrollable row
// with one set of step buttons rather than two competing scroll regions.
import React, { useEffect, useState } from 'react';

function PendingBadge({ count }) {
  if (!count) return null;
  return (
    <span className="ml-1.5 inline-flex items-center gap-1 align-middle">
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-destructive opacity-75" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-destructive" />
      </span>
      <span className="rounded-full bg-destructive/15 px-1.5 py-px text-[10px] font-semibold leading-tight text-destructive">
        {count}
      </span>
    </span>
  );
}

export default function DashboardSectionNav({
  items,
  scrollRootRef,
  onActiveChange,
  navRef,
  stuckHeightRef,
  onBeforeNavigate,
}) {
  const [active, setActive] = useState(items[0]?.id);

  useEffect(() => {
    onActiveChange?.(active);
  }, [active, onActiveChange]);

  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root) return undefined;

    const recompute = () => {
      const threshold = root.getBoundingClientRect().top + (stuckHeightRef?.current || 56) + 2;
      let current = items[0]?.id;
      for (const { id } of items) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= threshold) current = id;
      }
      setActive((prev) => (prev === current ? prev : current));
    };

    recompute();
    root.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    return () => {
      root.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
    };
  }, [items, scrollRootRef, stuckHeightRef]);

  return (
    <nav ref={navRef} className="flex items-center gap-1">
      {items.map((n) => {
        const isActive = active === n.id;
        return (
          <button
            key={n.id}
            type="button"
            onClick={() => {
              setActive(n.id);
              // This nav is always visible now, even from the Live-call
              // view where this dashboard sits CSS-hidden (display:none)
              // in the background rather than unmounted — onBeforeNavigate
              // switches it into view. Its sections have no layout yet in
              // this same synchronous tick, so the actual scroll waits a
              // couple of frames for that to commit first.
              onBeforeNavigate?.();
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  document.getElementById(n.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                });
              });
            }}
            className={`ap-tab-hover relative shrink-0 whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all ${
              isActive ? 'ap-tab-emboss text-foreground' : 'text-muted-foreground hover:text-foreground'
            }${n.blink && n.badge ? ' dpc-blink' : ''}`}
          >
            {n.label}
            {n.dotOnly ? (
              n.badge ? <span className="ml-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-destructive align-middle" /> : null
            ) : (
              <PendingBadge count={n.badge} />
            )}
          </button>
        );
      })}
    </nav>
  );
}
